import { useQuery } from '@apollo/client/react'
import {
  Button,
  Center,
  Heading,
  SimpleGrid,
  SimpleGridProps,
  Spinner,
  StackProps,
  Text,
  VStack,
} from '@chakra-ui/react'
import { EventCard, EventCardProps, eventCardGroupEventFragment } from '@src/components/sco/Events/EventCard'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { GroupEvent } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import { parseAsString, useQueryState } from 'nuqs'
import React, { useEffect, useState } from 'react'

type EventListProps = {
  allowLoadMore?: boolean
  eventCardProps?: EventCardProps
  isCurrentUserAttending?: boolean
  isFuture?: boolean
  isFree?: boolean
  search?: string
  order?: string[][]
  page?: number
  spacing?: StackProps['gap']
  pageSize?: number
  topicKeys?: string[]
  typeKeys?: string[]
  columns?: number | SimpleGridProps['columns']
  emptyListCta?: {
    title?: string
    ctaTitle?: string
    ctaHref?: string
  }
}

export function renderEvents(events: GroupEvent[] = [], eventCardProps: any) {
  return events.map((event, idx) => (
    <EventCard
      buttonStackProps={eventCardProps?.buttonStackProps}
      {...(eventCardProps || {})}
      key={idx}
      event={event}
    />
  ))
}

const groupEventsQuery = gql`
  query (
    $isFree: Boolean,
    $isCurrentUserAttending: Boolean,
    $isFuture: Boolean,
    $maximumStartsAt: DateTime,
    $minimumStartsAt: DateTime,
    $search: String,
    $order: [[String]],
    $page: Int,
    $pageSize: Int,
    $topicKeys: [String],
    $typeKeys: [String],
  ) {
    groupEvents(
      isFree: $isFree,
      isCurrentUserAttending: $isCurrentUserAttending
      isFuture: $isFuture,
      maximumStartsAt: $maximumStartsAt,
      minimumStartsAt: $minimumStartsAt,
      search: $search
      order: $order
      page: $page
      pageSize: $pageSize
      topicKeys: $topicKeys
      typeKeys: $typeKeys
    ) {
      id
      items {
        id
        ${eventCardGroupEventFragment}
      }
      pagination {
        hasNextPage
        itemCount
        page
        pageCount
        pageSize
      }
    }
  }
`

export const EventList = (props: EventListProps) => {
  const { getBreakpointValue } = useBreakpointContext()
  const [page, setPage] = useState(props.page || 1)
  const [search] = useQueryState('search', parseAsString)
  const activeSearch = props.search || search || null

  // New search terms should start from page 1.
  useEffect(() => {
    setPage(props.page || 1)
  }, [activeSearch, props.page])

  const query = useQuery<{ groupEvents?: { items?: GroupEvent[] } }>(groupEventsQuery, {
    variables: {
      isCurrentUserAttending: props.isCurrentUserAttending,
      page: page,
      pageSize: props.pageSize || 48,
      isFree: props.isFree,
      isFuture: props.isFuture,
      topicKeys: props.topicKeys,
      typeKeys: props.typeKeys,
      search: activeSearch,
      order: props.order,
    },
  })

  const events = query.data?.groupEvents?.items || []

  return (
    <>
      {query.error && (
        <Center padding={'1em'}>
          <Text>Event list failed to load.</Text>
        </Center>
      )}
      {!query.loading && !query.error && events?.length === 0 && props.emptyListCta && (
        <VStack gap={'2em'} paddingY={'4em'} width={'full'}>
          <Heading>{props.emptyListCta.title || 'No Matching Events'}</Heading>
          <Button asChild>
            <NextLink href={props.emptyListCta.ctaHref || '/events/calendar'}>
              {props.emptyListCta.ctaTitle || 'Browse Upcoming Events'}
            </NextLink>
          </Button>
        </VStack>
      )}
      <SimpleGrid
        columns={props.columns || getBreakpointValue([1, 1, 2, 3, 4])}
        gap={props.spacing || '20px'}
        width={'full'}
      >
        {renderEvents(events, props.eventCardProps)}
      </SimpleGrid>
      {query.loading ? (
        <Center padding={'24px'}>
          <Spinner />
        </Center>
      ) : props.allowLoadMore ? (
        <Center padding={'24px'}>
          <Button onClick={() => setPage(page => page + 1)}>Load More</Button>
        </Center>
      ) : (
        <div />
      )}
    </>
  )
}
